import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import { useApplication } from "./ApplicationContext";
import { toZonedTime } from "date-fns-tz";
import { getEasternMidnightUTC } from "./timezoneUtils";
import NitrogenBalanceDisplay from "./NitrogenBalanceDisplay";

function NitrogenManagementForm({ sectionData, hintText }) {
  // const [applicationType, setApplicationType] = useState("in-season");
  const [selectedCard, setSelectedCard] = useState(null);
  const [date, setDate] = useState("");
  const [error, setError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const [formSuccessMsg, setFormSuccessMsg] = useState("");
  const [amountError, setAmountError] = useState("");
  const [amount, setAmount] = useState("");
  const [nitrogenBalance, setNitrogenBalance] = useState(null);
  const [balanceRefreshTrigger, setBalanceRefreshTrigger] = useState(0);
  const [applications, setApplications] = useState([]);
  const teamName = localStorage.getItem("username");
  const [tableData, setTableData] = useState([]);
  const [subapplicationType, setSubApplicationType] = useState("");

  const token = localStorage.getItem("token");
  const [productOption, setProductOption] = useState("");
  const [displayDate, setDisplayDate] = useState(""); // for binding to date input

  const [dateToday, setDateToday] = useState(getEasternMidnightUTC());

  const [isProductOptionConfirmed, setIsProductOptionConfirmed] =
    useState(false);
  // const [ApplicationType, setApplicationType] = useState("None");
  // const [isApplicationTypeConfirmed, setIsApplicationTypeConfirmed] =
  //   useState(false);
  const {
    applicationType,
    setApplicationType,
    isApplicationTypeConfirmed,
    setIsApplicationTypeConfirmed,
  } = useApplication();

  const handleApplicationTypeConfirmation = (event) => {
    setIsApplicationTypeConfirmed(event.target.checked);
  };

  const handleConfirmApplicationType = () => {
    setIsApplicationTypeConfirmed(true);
    saveApplicationTypeConfirmationToBackend();
  };

  const saveApplicationTypeConfirmationToBackend = () => {
    const data = {
      teamName: teamName,
      applicationType: applicationType,
      isConfirmed: true,
    };

    axios
      .post("/api/saveApplicationTypeConfirmation", data, {
        headers: {
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      })
      .then((response) => {
        console.log("Application type confirmation saved:", response);
      })
      .catch((error) => {
        console.error("Error saving application type confirmation:", error);
      });
  };

  const handleProductOptionChange = (option) => {
    console.log(option);

    if (!isProductOptionConfirmed) {
      setProductOption(option);
    }
  };

  const handleProductOptionConfirmation = (event) => {
    setIsProductOptionConfirmed(event.target.checked);
    if (event.target.checked) {
      saveProductOptionConfirmationToBackend();
    }
  };

  const saveProductOptionConfirmationToBackend = () => {
    const data = {
      teamName: teamName,
      productOption: productOption,
      isConfirmed: true,
    };
    axios
      .post("/api/saveProductOptionConfirmation", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("Product option confirmation saved:", response);
      })
      .catch((error) => {
        console.error("Error saving product option confirmation:", error);
      });
  };

  const handleCardClick = (value) => {
    setApplicationType(value);
  };

  const inseasonOptions = [
    "Broadcast",
    "Banding",
    "Liquidside-Dressing",
    "Fertigation",
  ];

  //const controlledreleaseOptions = ["Broadcast", "Banding", "Incorporated"];
  const controlledreleaseOptions = ["Broadcast", "Banding"];
  const controlledreleaseProductOptions = [
    "Harrell's: 43-0-0",
    "Pursell: 43-0-0",
    "Florikan: 44-0-0",
  ];

  const handleClick = (selected) => {
    const subApplicationMapping = {
      Broadcast: "Broadcast",
      Banding: "Banding",
      Incorporated: "Incorporated",
      "Liquidside-Dressing": "Liquidside-Dressing",
      Fertigation: "Fertigation",
      "Harrell's: 43-0-0": "Harrell's: 43-0-0",
      "Pursell: 43-0-0": "Pursell: 43-0-0",
      "Florikan: 44-0-0": "Florikan: 44-0-0",
    };

    if (subApplicationMapping[selected]) {
      setSubApplicationType(subApplicationMapping[selected]);
    }

    setSelectedCard(selectedCard === selected ? null : selected);
    setFormSuccessMsg("");
  };

  useEffect(
    () => {
      // Fetch data from the backend when the component mounts
      fetchApplicationTypeConfirmation();
      fetchDataFromBackend();
      fetchProductOptionConfirmation();
      fetchNitrogenBalance();
    },
    [applicationType],
    [productOption],
  ); // Fetch data whenever applicationType changes

  const fetchNitrogenBalance = async () => {
    try {
      const response = await axios.get("/api/getNitrogenBalance", {
        params: { teamName, cropType: "corn" },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setNitrogenBalance(response.data);
      }
    } catch (error) {
      console.error("Error fetching nitrogen balance:", error);
    }
  };

  const fetchProductOptionConfirmation = () => {
    axios
      .get("/api/getProductOptionConfirmation", {
        params: { teamName },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.status === 200 && response.data) {
          setProductOption(response.data.productOption);
          setIsProductOptionConfirmed(response.data.isConfirmed);
        }
      })
      .catch((error) => {
        console.error("Error fetching product option confirmation:", error);
      });
  };

  const fetchApplicationTypeConfirmation = () => {
    axios
      .get("/api/getApplicationTypeConfirmation", {
        params: { teamName: teamName },
        headers: {
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data) {
          // Assuming response.data has the structure { applicationType, isConfirmed }
          setApplicationType(response.data.applicationType);
          setIsApplicationTypeConfirmed(response.data.isConfirmed);
        } else {
          console.error("Application type confirmation not found");
        }
      })
      .catch((error) => {
        console.error("Error fetching application type confirmation:", error);
      });
  };

  const handleDeleteApplication = async (appId, appAmount) => {
    try {
      // First, restore the nitrogen balance
      await axios.post(
        "/api/restoreNitrogenBalance",
        {
          teamName,
          cropType: "corn",
          amount: appAmount,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Then, delete the application
      const response = await axios.delete(
        `/api/deletenitrogenApplication/${appId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200) {
        fetchDataFromBackend();
        fetchNitrogenBalance(); // Refresh balance
        setBalanceRefreshTrigger((prev) => prev + 1); // Trigger component refresh
      } else {
        console.error("Failed to delete the application");
      }
    } catch (error) {
      console.error("Error deleting the application:", error);
    }
  };

  const fetchDataFromBackend = () => {
    // Make an API request to fetch data from the backend based on the application type
    axios
      .get("/api/fetchNitrogenManagementData", {
        params: {
          teamName,
          applicationType: applicationType,
        },
        headers: {
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      })
      .then((response) => {
        if (response.status === 200) {
          // Set the retrieved data to the state variable
          setTableData(response.data);
        } else {
          console.error("Failed to fetch data from the backend");
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleAddApplication = async (e) => {
    e.preventDefault();
    setFormErrorMsg("");
    setFormSuccessMsg("");

    // Check nitrogen balance before adding
    if (
      nitrogenBalance &&
      parseFloat(amount) > parseFloat(nitrogenBalance.remaining)
    ) {
      setFormErrorMsg(
        `Insufficient nitrogen balance. You only have ${nitrogenBalance.remaining} lbs/ac remaining.`,
      );
      return;
    }

    // Create a new application object
    let newApplication;

    if (applicationType === "in-season") {
      // Customize the object for in-season type
      newApplication = {
        applicationType: applicationType,
        date: date,
        amount: amount,
        placement: subapplicationType, // Initialize as an empty array
        teamName,
        applied: "no",
        dateToday: dateToday,
        productOption: "",
      };

      // Add placement options to the array if they are true
      // if (broadcast) newApplication.placement.push("Broadcast");
      // if (sideDressed) newApplication.placement.push("Side-dressed");
      // if (liquidSideDressing)
      //   newApplication.placement.push("Liquid Side-dressing");

      // // Convert the placement array to a comma-separated string
      // newApplication.placement = newApplication.placement.join(", ");
    } else if (applicationType === "controlled-release") {
      // Customize the object for controlled-release type
      newApplication = {
        applicationType: applicationType,
        // Add additional fields specific to controlled-release type
        // Example: controlledReleaseField: controlledReleaseValue,
        date: date,
        amount: amount,
        placement: subapplicationType,
        teamName,
        applied: "no",
        dateToday: dateToday,
        productOption: productOption,
      };
    }

    // Add the new application to the local state
    setApplications([...applications, newApplication]);

    // Clear the form fields
    setDate("");
    setAmount("");

    // Send the new application data to the backend
    sendApplicationDataToBackend(newApplication);
  };

  // Function to send application data to the backend using Axios
  const sendApplicationDataToBackend = async (applicationData) => {
    try {
      // First, update the nitrogen balance
      const balanceResponse = await axios.post(
        "/api/updateNitrogenBalance",
        {
          teamName,
          cropType: "corn",
          amount: applicationData.amount,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (balanceResponse.status !== 200) {
        setFormErrorMsg("Failed to update nitrogen balance");
        return;
      }

      // Then, submit the application
      const response = await axios.post(
        "/api/nitrogenmanagementsubmit",
        applicationData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200) {
        console.log("Application data sent to the backend successfully");
        setFormSuccessMsg("Input submitted successfully!");
        fetchDataFromBackend();
        fetchNitrogenBalance(); // Refresh balance
        setBalanceRefreshTrigger((prev) => prev + 1); // Trigger component refresh
      } else {
        console.error("Failed to send application data to the backend");
        setFormErrorMsg("Failed to submit application");
      }
    } catch (error) {
      console.error("Error sending application data:", error);
      if (error.response?.data?.message) {
        setFormErrorMsg(error.response.data.message);
      } else {
        setFormErrorMsg("Error submitting application");
      }
    }
  };

  const usHolidays = [
    "2026-01-01", // New Year's Day
    "2026-07-04", // Independence Day
    "2026-11-11", // Veterans Day
    "2026-12-25", // Christmas Day
  ];

  const getDynamicHolidays = (year) => {
    const holidays = [];

    // // Martin Luther King Jr. Day (Third Monday of January)
    // const mlkDay = new Date(year, 0, 1);
    // while (mlkDay.getDay() !== 1) mlkDay.setDate(mlkDay.getDate() + 1);
    // mlkDay.setDate(mlkDay.getDate() + 14); // Third Monday
    // holidays.push(mlkDay.toISOString().split("T")[0]);

    // // Presidents' Day (Third Monday of February)
    // const presidentsDay = new Date(year, 1, 1);
    // while (presidentsDay.getDay() !== 1)
    //   presidentsDay.setDate(presidentsDay.getDate() + 1);
    // presidentsDay.setDate(presidentsDay.getDate() + 14);
    // holidays.push(presidentsDay.toISOString().split("T")[0]);

    // // Memorial Day (Last Monday of May)
    // const memorialDay = new Date(year, 4, 31);
    // while (memorialDay.getDay() !== 1)
    //   memorialDay.setDate(memorialDay.getDate() - 1);
    // holidays.push(memorialDay.toISOString().split("T")[0]);

    // // Labor Day (First Monday of September)
    // const laborDay = new Date(year, 8, 1);
    // while (laborDay.getDay() !== 1) laborDay.setDate(laborDay.getDate() + 1);
    // holidays.push(laborDay.toISOString().split("T")[0]);

    // // Thanksgiving (Fourth Thursday of November)
    // const thanksgiving = new Date(year, 10, 1);
    // while (thanksgiving.getDay() !== 4)
    //   thanksgiving.setDate(thanksgiving.getDate() + 1);
    // thanksgiving.setDate(thanksgiving.getDate() + 21);
    // holidays.push(thanksgiving.toISOString().split("T")[0]);

    return holidays;
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const dateObj = new Date(selectedDate);
    const dayOfWeek = dateObj.getDay();
    const year = dateObj.getFullYear();

    const allHolidays = [...usHolidays, ...getDynamicHolidays(year)];

    if (dayOfWeek === 5 || dayOfWeek === 6) {
      setError(true);
      setErrorMessage("No Nitrogen inputs can be applied on weekends.");
    } else if (allHolidays.includes(selectedDate)) {
      setError(true);
      setErrorMessage("Selected date is a U.S. public holiday.");
    } else {
      setError(false);
      setErrorMessage("");

      const utcDate = getUtcFromLocalESTDate(selectedDate);
      setDate(utcDate); // ✅ backend value
      setDisplayDate(selectedDate); // ✅ input field display value
    }
  };

  const getUtcFromLocalESTDate = (dateStr) => {
    const timeZone = "America/New_York";
    const localMidnight = new Date(`${dateStr}T00:00:00`);
    const zoned = toZonedTime(localMidnight, timeZone);
    return new Date(zoned).toISOString(); // This will be like 2026-06-01T04:00:00.000Z
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);

    if (value && parseFloat(value) < 30) {
      setAmountError("Nitrogen inputs should be more than 30 lbs/ac");
    } else {
      setAmountError("");
    }
  };

  // Check if balance will be exceeded
  const isBalanceExceeded =
    nitrogenBalance &&
    amount &&
    parseFloat(amount) > parseFloat(nitrogenBalance.remaining);

  return (
    <div>
      <form onSubmit={handleAddApplication}>
        {/* <Typography variant="h6" gutterBottom>
        Add Application
      </Typography> */}
        {/* <div style={{ display: "flex", flexDirection: "row" }}>
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="applicationType"
            name="applicationType"
            value={applicationType}
            onChange={(e) => setApplicationType(e.target.value)}
          >
            <FormControlLabel
              value="in-season"
              control={<Radio />}
              label="In-season Fertilizer Application"
            />
            <FormControlLabel
              value="controlled-release"
              control={<Radio />}
              label="Controlled-Release Fertilizer"
            />
          </RadioGroup>
        </FormControl>
      </div> */}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Card
              onClick={() =>
                !isApplicationTypeConfirmed && handleCardClick("in-season")
              }
              sx={{
                cursor: !isApplicationTypeConfirmed ? "pointer" : "not-allowed",
                margin: "4px",
                padding: "10px 16px",
                backgroundColor:
                  applicationType === "in-season" ? "#fa4616" : "#F5F5F5",
                border:
                  applicationType === "in-season"
                    ? "2px solid rgb(255, 255, 255)"
                    : "2px solid rgb(37, 106, 185)",
                borderRadius: "12px",
                color: applicationType === "in-season" ? "white" : "#333",
                boxShadow:
                  applicationType === "in-season"
                    ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                    : "none",
                transition: "all 0.3s ease-in-out",
                display: "flex",
                justifyContent: "center", // Center horizontally
                alignItems: "center", // Center vertically
                textAlign: "center", // Ensures text stays centered
                height: "50px", // Fixed height for better alignment
                opacity:
                  isApplicationTypeConfirmed && applicationType !== "in-season"
                    ? 0.5
                    : 1,
                "&:hover": {
                  backgroundColor:
                    applicationType === "in-season" ? "#d73a12" : "#E0E0E0",
                  transform: !isApplicationTypeConfirmed
                    ? "scale(1.05)"
                    : "none",
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                  }}
                >
                  In-season Fertilizer Application
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card
              onClick={() =>
                !isApplicationTypeConfirmed &&
                handleCardClick("controlled-release")
              }
              sx={{
                cursor: !isApplicationTypeConfirmed ? "pointer" : "not-allowed",
                margin: "4px",
                padding: { xs: "8px", sm: "10px 16px" },
                backgroundColor:
                  applicationType === "controlled-release"
                    ? "#fa4616"
                    : "#F5F5F5",
                border:
                  applicationType === "controlled-release"
                    ? "2px solid rgb(255, 255, 255)"
                    : "2px solid rgb(37, 106, 185)",
                borderRadius: "12px",
                color:
                  applicationType === "controlled-release" ? "white" : "#333",
                boxShadow:
                  applicationType === "controlled-release"
                    ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                    : "none",
                transition: "all 0.3s ease-in-out",
                display: "flex",
                justifyContent: "center", // Center horizontally
                alignItems: "center", // Center vertically
                textAlign: "center", // Ensures text stays centered
                height: "50px", // Fixed height for better alignment
                opacity:
                  isApplicationTypeConfirmed &&
                  applicationType !== "controlled-release"
                    ? 0.5
                    : 1,
                "&:hover": {
                  backgroundColor:
                    applicationType === "controlled-release"
                      ? "#d73a12"
                      : "#E0E0E0",
                  transform: !isApplicationTypeConfirmed
                    ? "scale(1.05)"
                    : "none",
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                  }}
                >
                  Controlled-Release Fertilizer
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <FormControlLabel
          control={
            <Checkbox
              checked={isApplicationTypeConfirmed}
              onChange={(event) => {
                handleApplicationTypeConfirmation(event);
                if (event.target.checked) {
                  handleConfirmApplicationType();
                }
              }}
              disabled={isApplicationTypeConfirmed || !applicationType}
            />
          }
          label="Confirm application type by ticking the checkbox (application type cannot be changed later once check box is ticked)"
        />

        {applicationType === "in-season" && (
          <>
            <p style={{ textAlign: "justify" }}>
              Choose the application method, date, and amount of nitrogen (N) to
              apply. From planting until the V8 growth stage, teams may apply
              dry ammonium nitrate using either broadcasting or banding methods.
            </p>
            <p style={{ textAlign: "justify" }}>
              After V8, teams will also have the option to apply nitrogen
              through fertigation or liquid side-dressing using Y-drops (Miller
              Highboy) of UAN 28%. Liquid side-dressing and fertigation
              applications can be made once per week on a fixed day (Thursday).
            </p>
            <p style={{ textAlign: "justify" }}>
              The minimum nitrogen amount per application is 30 lbs/ac.
              Additionally, the total nitrogen applied during the season must
              not exceed 240 lbs/ac, in accordance with FDACS nutrient
              management guidelines.
            </p>
            <p style={{ textAlign: "justify" }}>
              Please refer to the fertilizer counter on the portal, which
              displays the remaining nitrogen balance, and plan your application
              splits accordingly.
            </p>

            <p style={{ textAlign: "justify" }}>
              In case of a leaching rain event (determined by the project
              management team), an additional application of N at 30 lbs/ac will
              be allowed. Participating teams will be responsible to communicate
              their decision at least three days in advance.
            </p>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="h5">
                  In season application methods:
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {inseasonOptions.map((option) => (
                    <Card
                      key={option}
                      onClick={() => handleClick(option)}
                      sx={{
                        cursor: "pointer",
                        margin: "4px",
                        padding: "10px 16px",
                        backgroundColor:
                          selectedCard === option ? "#fa4616" : "#F5F5F5",
                        border:
                          selectedCard === option
                            ? "2px solid rgb(255, 255, 255)"
                            : "2px solid rgb(37, 106, 185)",
                        borderRadius: "12px",
                        color: selectedCard === option ? "white" : "#333",
                        boxShadow:
                          selectedCard === option
                            ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                            : "none",
                        transition: "all 0.3s ease-in-out",
                        display: "flex",
                        justifyContent: "center", // Center horizontally
                        alignItems: "center", // Center vertically
                        textAlign: "center", // Ensures text stays centered
                        height: "50px", // Fixed height for better alignment
                        "&:hover": {
                          backgroundColor:
                            selectedCard === option ? "#d73a12" : "#E0E0E0",
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="body2"
                          sx={{ textAlign: "center" }}
                        >
                          {option}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Application Method
                  </Typography>
                  <TextField
                    labelId="subapplicationtype-label"
                    id="subapplicationtype"
                    label="Application Method"
                    variant="outlined"
                    fullWidth
                    value={subapplicationType}
                    onChange={(e) => setSubApplicationType(e.target.value)}
                    placeholder="Application Type"
                    required
                    sx={{ mb: 2 }}
                    InputProps={{ readOnly: true }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="date-input"
                        label="Date"
                        variant="outlined"
                        fullWidth
                        type="date"
                        value={displayDate}
                        onChange={handleDateChange}
                        required
                        InputLabelProps={{ shrink: true, required: true }}
                        error={error}
                        helperText={error ? errorMessage : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        id="amount-input"
                        label="Amount (lbs/ac of N)"
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={amount}
                        onChange={handleAmountChange}
                        placeholder="Enter amount (minimum 30 lbs/ac)"
                        required
                        inputProps={{ min: 30, step: 1 }}
                        error={!!amountError}
                        helperText={amountError}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              lbs/ac
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <NitrogenBalanceDisplay
                  cropType="corn"
                  currentAmount={amount}
                  refreshTrigger={balanceRefreshTrigger}
                />
              </Grid>
            </Grid>
            <br></br>
          </>
        )}

        {applicationType === "controlled-release" && (
          <>
            <p style={{ textAlign: "justify" }}>
              Controlled-Release Fertilizer Program: For the CRF program, you
              can choose any CRF rate ranging from 150 to 240 lbs/ac of N. All
              the CRF applications will be applied at planting.
            </p>

            <p style={{ textAlign: "justify" }}>
              In case of a leaching rain event (determined by the project
              management team), an additional application of N at 30 lbs/ac will
              be allowed. Participating teams will be responsible to communicate
              their decision at least three days in advance.
            </p>

            <Grid item xs={12}>
              <Typography variant="h5">
                Controlled Release Fertilizer Product Options:
              </Typography>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {controlledreleaseProductOptions.map((option) => {
                  // Define the display text with line breaks
                  const displayText = {
                    "Harrell's: 43-0-0": ["Harrell's: 43-0-0", "(90 DR)"],
                    "Pursell: 43-0-0": ["Pursell: 43-0-0", "(120 DR)"],
                    "Florikan: 44-0-0": ["Florikan: 44-0-0", "(90 DR)"],
                  };

                  return (
                    <Card
                      key={option}
                      onClick={() =>
                        !isProductOptionConfirmed &&
                        handleProductOptionChange(option)
                      }
                      sx={{
                        cursor: !isProductOptionConfirmed
                          ? "pointer"
                          : "not-allowed",
                        margin: "4px",
                        padding: "10px 16px",
                        backgroundColor:
                          productOption === option ? "#fa4616" : "#F5F5F5",
                        border:
                          productOption === option
                            ? "2px solid rgb(255, 255, 255)"
                            : "2px solid rgb(37, 106, 185)",
                        borderRadius: "12px",
                        color: productOption === option ? "white" : "#333",
                        boxShadow:
                          productOption === option
                            ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                            : "none",
                        transition: "all 0.3s ease-in-out",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        minHeight: "70px",
                        opacity:
                          isProductOptionConfirmed && productOption !== option
                            ? 0.5
                            : 1,
                        "&:hover": {
                          backgroundColor:
                            productOption === option ? "#d73a12" : "#E0E0E0",
                          transform: !isProductOptionConfirmed
                            ? "scale(1.05)"
                            : "none",
                        },
                      }}
                    >
                      <CardContent sx={{ padding: "8px !important" }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {displayText[option][0]}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.85rem" }}
                        >
                          {displayText[option][1]}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  sx={{ paddingBottom: 2 }}
                  checked={isProductOptionConfirmed}
                  onChange={handleProductOptionConfirmation}
                  disabled={isProductOptionConfirmed || !productOption}
                />
              }
              label="Confirm product option by ticking the checkbox (product option cannot be changed later once check box is ticked)"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="h5">
                  Controlled Release Application Methods:
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {controlledreleaseOptions.map((option) => (
                    <Card
                      key={option}
                      onClick={() => handleClick(option)}
                      sx={{
                        cursor: "pointer",
                        margin: "4px",
                        padding: "10px 16px",
                        backgroundColor:
                          selectedCard === option ? "#fa4616" : "#F5F5F5",
                        border:
                          selectedCard === option
                            ? "2px solid rgb(255, 255, 255)"
                            : "2px solid rgb(37, 106, 185)",
                        borderRadius: "12px",
                        color: selectedCard === option ? "white" : "#333",
                        boxShadow:
                          selectedCard === option
                            ? "0px 4px 10px rgba(0, 0, 0, 0.2)"
                            : "none",
                        transition: "all 0.3s ease-in-out",
                        display: "flex",
                        justifyContent: "center", // Center horizontally
                        alignItems: "center", // Center vertically
                        textAlign: "center", // Ensures text stays centered
                        height: "50px", // Fixed height for better alignment
                        "&:hover": {
                          backgroundColor:
                            selectedCard === option ? "#d73a12" : "#E0E0E0",
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2">{option}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Application Method
                  </Typography>
                  <TextField
                    labelId="subapplicationtype-label"
                    id="subapplicationtype"
                    label="Application Method"
                    variant="outlined"
                    fullWidth
                    value={subapplicationType}
                    onChange={(e) => setSubApplicationType(e.target.value)}
                    placeholder="Application Type"
                    required
                    sx={{ mb: 2 }}
                    InputProps={{ readOnly: true }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="date-input"
                        label="Date"
                        variant="outlined"
                        fullWidth
                        type="date"
                        value={displayDate}
                        onChange={handleDateChange}
                        required
                        InputLabelProps={{ shrink: true, required: true }}
                        error={error}
                        helperText={error ? errorMessage : ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        id="amount-input"
                        label="Amount (lbs/ac of N)"
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={amount}
                        onChange={handleAmountChange}
                        placeholder="Enter amount (minimum 30 lbs/ac)"
                        required
                        inputProps={{ min: 30, step: 1 }}
                        error={!!amountError}
                        helperText={amountError}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              lbs/ac
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <NitrogenBalanceDisplay
                  cropType="corn"
                  currentAmount={amount}
                  refreshTrigger={balanceRefreshTrigger}
                />
              </Grid>
            </Grid>
            <br></br>
          </>
        )}
        {isBalanceExceeded && (
          <Typography
            variant="body2"
            sx={{ color: "red", mb: 1, fontWeight: "bold" }}
          >
            ⚠️ Input exceeding season limit
          </Typography>
        )}
        {/* <Button
          id={`n2-mgmnt-${sectionData}-add`}
          variant="contained"
          color="primary"
          onClick={handleAddApplication}
        >
          Add Application
        </Button> */}
        <Button
          id={`n2-mgmnt-${sectionData}-add`}
          variant="contained"
          color="primary"
          type="submit"
          disabled={
            !isApplicationTypeConfirmed ||
            !date ||
            !amount ||
            !!amountError ||
            parseFloat(amount) < 30 ||
            isBalanceExceeded ||
            !subapplicationType ||
            (applicationType === "controlled-release" &&
              !isProductOptionConfirmed)
          }
        >
          Add Application
        </Button>
        {formErrorMsg && (
          <Typography variant="body2" sx={{ color: "red", mt: 1 }}>
            {formErrorMsg}
          </Typography>
        )}
        {formSuccessMsg && (
          <Typography variant="body2" sx={{ color: "green", mt: 1 }}>
            {formSuccessMsg}
          </Typography>
        )}
      </form>
      <Typography variant="h6" gutterBottom>
        Application Data
      </Typography>
      <TableContainer component={Paper}>
        <Table id={`table-n2-mgmnt-${sectionData}`}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>

              <TableCell>Application Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>placement</TableCell>
              <TableCell>Product Option</TableCell>
              <TableCell>Applied</TableCell>
              <TableCell>
                <EditIcon />
              </TableCell>
              {/* {applicationType === "in-season" && (
                <TableCell>placement</TableCell>
              )} */}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...tableData]
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map((app, index) => {
                // Format the date to display only the date part (YYYY-MM-DD)
                //const formattedDate = new Date(app.date.substring(0, 10)).toLocaleDateString();

                return (
                  <TableRow key={app.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {new Date(app.date).toLocaleDateString("en-US", {
                        timeZone: "America/New_York",
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>{app.amount}</TableCell>
                    <TableCell>{app.placement}</TableCell>
                    <TableCell>{app.productOption}</TableCell>
                    <TableCell>
                      <button
                        style={{
                          backgroundColor:
                            app.applied === "no" ? "red" : "green",
                          color: "white", // Assuming you want white text for contrast
                          border: "none", // Remove default button border styling
                          // Add any other styling you need here
                        }}
                      >
                        {app.applied === "no" ? (
                          <HighlightOffIcon />
                        ) : (
                          <DoneIcon />
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button>
                        {app.applied === "no" ? (
                          <DeleteIcon
                            onClick={() =>
                              handleDeleteApplication(app.id, app.amount)
                            }
                          />
                        ) : (
                          <EditOffIcon />
                        )}
                      </button>
                    </TableCell>

                    {/*  {applicationType === "in-season" && (
                  <TableCell>{app.placement}</TableCell> 
                )}*/}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default NitrogenManagementForm;
